// Home-built ROM picker — a "file browser" modal for the player overlay.
//
// The Load ROM button opens this instead of the OS file dialog. It lists
// the games you've already loaded, grouped into a folder per console (so
// they read as a tidy file tree rather than a loose row of buttons), and
// carries a "Load from my computer" button that hands off to the native
// file picker for a brand-new ROM.
//
// Built as a native <dialog> created on demand (no markup in index.html):
// the harness constructs one instance at boot and calls open(); each open
// re-reads the saved-ROM index so the list reflects the latest loads.

import { readRecentRoms, type RecentRom } from "./rom-cache.js";
import { coreLabelForName } from "./cores/registry.js";

// What the picker needs from the rest of the player: hand off to the OS
// file dialog for a new ROM, or load one the user already has saved.
export interface RomPickerModalDeps {
  // Open the native <input type=file> picker for a fresh ROM. The modal
  // closes itself first so the OS dialog takes over a clean screen.
  loadFromComputer: () => void;
  // Launch a previously-saved ROM. Resolves null on success (the modal then
  // closes); resolves an error message to show inline (the modal stays open)
  // when the ROM can't be loaded — its cached bytes were evicted, or it doesn't
  // match the game a joined session requires.
  chooseSaved: (entry: RecentRom) => Promise<string | null>;
}

// Folder glyph for a console group header.
const FOLDER_ICON =
  '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 6a2 2 0 0 1 2-2h3.5l2 2H19a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6z"/></svg>';
// Cartridge glyph for a saved-game row.
const CART_ICON =
  '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="5" y="3" width="14" height="18" rx="1.5"/><path d="M9 3v4h6V3"/><path d="M8.5 12h7M8.5 15h7"/></svg>';

export class RomPickerModal {
  private dialog: HTMLDialogElement;
  private body: HTMLElement;
  private note: HTMLElement;
  private deps: RomPickerModalDeps;

  constructor(deps: RomPickerModalDeps) {
    this.deps = deps;

    this.dialog = document.createElement("dialog");
    this.dialog.className = "picker-modal";
    this.dialog.setAttribute("aria-labelledby", "picker-modal-title");

    const head = document.createElement("div");
    head.className = "modal-head";
    const title = document.createElement("h2");
    title.id = "picker-modal-title";
    title.textContent = "Load a game";
    const close = document.createElement("button");
    close.type = "button";
    close.className = "modal-close";
    close.setAttribute("aria-label", "Close");
    close.textContent = "×";
    close.addEventListener("click", () => this.dialog.close());
    head.append(title, close);

    this.body = document.createElement("div");
    this.body.className = "modal-body picker-body";

    // Inline status line for the rare "cached bytes are gone" case.
    this.note = document.createElement("p");
    this.note.className = "picker-note";
    this.note.hidden = true;

    const footer = document.createElement("div");
    footer.className = "modal-footer";
    // A spacer keeps the single button right-aligned (modal-footer uses
    // space-between, mirroring the controls modal's two-button row).
    footer.append(document.createElement("span"));
    const fromComputer = document.createElement("button");
    fromComputer.type = "button";
    fromComputer.className = "primary-btn";
    fromComputer.textContent = "Load from my computer…";
    fromComputer.addEventListener("click", () => {
      this.dialog.close();
      this.deps.loadFromComputer();
    });
    footer.append(fromComputer);

    this.dialog.append(head, this.body, this.note, footer);
    document.body.append(this.dialog);

    // Click on the backdrop (outside the dialog content) to dismiss.
    this.dialog.addEventListener("click", (e) => {
      if (e.target === this.dialog) this.dialog.close();
    });
  }

  open(): void {
    if (this.dialog.open) return;
    this.note.hidden = true;
    this.render();
    this.dialog.showModal();
  }

  // Open the picker showing an error note at the foot. Used by the file-pick
  // path, whose native dialog runs OUTSIDE this modal (loadFromComputer closes
  // it first), so a rejected pick — e.g. a ROM that doesn't match a joined
  // session's game — reopens the picker here with the reason and the library to
  // try again.
  showError(message: string): void {
    this.render();
    this.note.textContent = message;
    this.note.hidden = false;
    if (!this.dialog.open) this.dialog.showModal();
  }

  private render(): void {
    this.body.innerHTML = "";
    const saved = readRecentRoms();

    if (saved.length === 0) {
      const empty = document.createElement("p");
      empty.className = "picker-empty";
      empty.textContent = "No saved games yet — load one from your computer to get started.";
      this.body.append(empty);
      return;
    }

    // Group by console, preserving recency order: a Map keyed by the
    // folder label keeps insertion order, so the folder holding your most
    // recently played game floats to the top, and rows within stay newest-
    // first (readRecentRoms is already most-recent-first).
    const groups = new Map<string, RecentRom[]>();
    for (const entry of saved) {
      const label = coreLabelForName(entry.name);
      const bucket = groups.get(label);
      if (bucket) bucket.push(entry);
      else groups.set(label, [entry]);
    }

    for (const [label, entries] of groups) {
      this.body.append(this.renderFolder(label, entries));
    }
  }

  private renderFolder(label: string, entries: RecentRom[]): HTMLElement {
    const folder = document.createElement("div");
    folder.className = "picker-folder";

    const header = document.createElement("div");
    header.className = "picker-folder-head";
    const icon = document.createElement("span");
    icon.className = "picker-icon";
    icon.innerHTML = FOLDER_ICON;
    const name = document.createElement("span");
    name.className = "picker-folder-name";
    name.textContent = label;
    const count = document.createElement("span");
    count.className = "picker-folder-count";
    count.textContent = String(entries.length);
    header.append(icon, name, count);
    folder.append(header);

    for (const entry of entries) {
      folder.append(this.renderRow(entry));
    }
    return folder;
  }

  private renderRow(entry: RecentRom): HTMLElement {
    const row = document.createElement("button");
    row.type = "button";
    row.className = "picker-row";
    row.title = entry.name;

    const icon = document.createElement("span");
    icon.className = "picker-icon";
    icon.innerHTML = CART_ICON;
    const name = document.createElement("span");
    name.className = "picker-row-name";
    name.textContent = entry.name;
    row.append(icon, name);

    row.addEventListener("click", () => {
      void (async () => {
        const error = await this.deps.chooseSaved(entry);
        if (error === null) {
          this.dialog.close();
        } else {
          this.note.textContent = error;
          this.note.hidden = false;
        }
      })().catch((e: unknown) => console.error("[harness] saved-ROM launch failed:", e));
    });
    return row;
  }
}
